import { gql, useApolloClient } from '@apollo/client';
import { Modal } from 'antd';
import { dispatch } from 'd3';
import React, {
  useState,
  useReducer,
  useEffect,
  useRef,
  Dispatch,
  useContext,
  createContext,
} from 'react';
import { joinArea } from '../../controller/client/PartyClient';
import { getIdToken } from '../../controller/PartyDB';
import {
  TAreaKey,
  IParticipantForUser,
  TUserId,
  TPartyKey,
  EAreaPrefix,
} from '../../PartyClientResponse';
import { initialState } from '../../redux/reducer';
import FirestoreService from '../../services/FirestoreService';
import FunctionsService from '../../services/FunctionsService';
import { useAppState } from '../../state/AppState';
import UserName from '../common/UserName';
import ChallengeGameClient from './ChallengeGameClient';
import GameData from './data/GameData';
import CreateGameViewModel from './models/CreateGameViewModel';
import GameEventModel from './models/GameEventModel';
import GameModel, {
  GameState,
  gameStateToString,
  GameType,
  gameTypeToString,
} from './models/GameModel';
import PlayerModel, { CreatePlayerViewModel } from './models/PlayerModel';

export enum GameDetailActionType {
  Hello,
  SET_GAME_COLLECTION,
  SET_CURRENT_GAME_ID,
  SET_GAME_DETAIL,
  SET_GAME_PLAYERS,
  SET_GAME_EVENTS,
  RESET_STORE,
}

type TGameAction =
  | {
      type: GameDetailActionType.Hello;
      hello: string;
    }
  | {
      type: GameDetailActionType.SET_CURRENT_GAME_ID;
      selectedGameId: string | null;
    }
  | {
      type: GameDetailActionType.SET_GAME_COLLECTION;
      collection: GameModel[];
    }
  | {
      type: GameDetailActionType.SET_GAME_DETAIL;
      detail: GameModel | null;
    }
  | {
      type: GameDetailActionType.SET_GAME_PLAYERS;
      players: PlayerModel[];
    }
  | {
      type: GameDetailActionType.SET_GAME_EVENTS;
      events: GameEventModel[];
    }
  | {
      type: GameDetailActionType.RESET_STORE;
    };

interface CurrentGame {
  detail: GameModel | null;
  players: PlayerModel[];
  events: GameEventModel[];
}

interface IGameDetailContextState {
  detail: GameModel;
  players: PlayerModel[];
  events: GameEventModel[];
}
interface IGameCollectionContextState {
  gameItems: GameModel[];
  selectedGameDetail: GameModel | null;
  // selectedGameId: string | null;
  selectedGamePlayers: PlayerModel[];
  selectedGameEvents: GameEventModel[];
}

interface IGameProviderProps {
  gameDataState: IGameCollectionContextState;
  gameDataError: string;
  gameDataDispatch: Dispatch<TGameAction>;
  startGame: () => void;
  addPoint: (player: PlayerModel) => void;
  removePoint: (player: PlayerModel) => void;
  endGame: () => void;
  createGame: () => void;
  isOwner: boolean;
  leaveGame: () => Promise<void>;
  clearGameFromRoom: () => Promise<void>;
  setGameDisplayName: (name: string) => Promise<void>;
}

function gameListReducer(state: IGameCollectionContextState, action: TGameAction) {
  switch (action.type) {
    case GameDetailActionType.SET_GAME_COLLECTION:
      return {
        ...state,
        gameItems: action.collection,
      };
    // case GameDetailActionType.SET_CURRENT_GAME_ID:
    //   return {
    //     ...state,
    //     selectedGameId: action.selectedGameId,
    //   };
    case GameDetailActionType.SET_GAME_DETAIL: {
      return {
        ...state,
        selectedGameDetail: action.detail,
      };
    }
    case GameDetailActionType.SET_GAME_PLAYERS:
      return {
        ...state,
        selectedGamePlayers: action.players,
      };
    case GameDetailActionType.SET_GAME_EVENTS:
      return {
        ...state,
        selectedGameEvents: action.events,
      };
    case GameDetailActionType.RESET_STORE:
      return initialGameReducerState;
    default:
      throw new Error();
  }
}

const gameData = new GameData(new FirestoreService(), new FunctionsService());

const initialGameReducerState = {
  gameItems: [],
  selectedGameDetail: null,
  selectedGamePlayers: [],
  selectedGameEvents: [],
};

const useChallengeGameState = () => {
  const {
    participantsByArea: allParticipantsByArea,
    currentArea,
    party,
    currentParticipant,
  } = useAppState();
  const areaParticipants = allParticipantsByArea[currentArea as TAreaKey] || [];
  const [gameDataError, setError] = useState('');
  const [gameClient, setGameClient] = useState<ChallengeGameClient | null>(null);
  const [gameDataState, gameDataDispatch] = useReducer(gameListReducer, initialGameReducerState);
  const client = useApolloClient();

  const isOwner = gameDataState.selectedGameDetail?.ownerId === currentParticipant?.uid;

  const prevGameState = useRef<GameState | null>(null);
  const prevGameId = useRef<string | null>(null);
  const currentGameId = gameDataState.selectedGameDetail?.id;
  const currentGameState = gameDataState.selectedGameDetail?.state;
  const [modal, contextHolder] = Modal.useModal();
  const hostName =
    areaParticipants.find((p) => p.uid === gameDataState.selectedGameDetail?.ownerId)
      ?.displayName || 'the host';
  const gameName = gameDataState.selectedGameDetail?.displayName || 'the game';
  useEffect(() => {
    if (
      currentGameId === prevGameId.current &&
      prevGameState.current !== GameState.STARTED &&
      currentGameState === GameState.STARTED
    ) {
      modal.info({
        width: '40vw',
        title: <div className='game-modal-title'>{gameName}</div>,
        content: (
          <div className='game-modal-content'>
            <div className='game-modal-item'>
              <UserName name={hostName} />
              {' has started a game!'}
            </div>
            <div className='game-modal-item'>
              Once you join the game, <UserName name={hostName} /> will tell you the rules.
            </div>
            <div className='game-modal-item'>
              Points are awarded by committee or by the host's judgement. The winner is decided by
              who has the most points at the end
            </div>
          </div>
        ),
        maskClosable: true,
        icon: '',
      });
    }
    prevGameState.current = currentGameState || null;
    prevGameId.current = currentGameId || null;
  }, [currentGameId, currentGameState, modal, gameName, hostName]);

  useEffect(() => {
    const unsubscribe = gameData.streamGameCollection({
      next: (games: GameModel[]) => {
        gameDataDispatch({ type: GameDetailActionType.SET_GAME_COLLECTION, collection: games });
      },
      error: () => setError('game-collection-get-error'),
    });
    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    (async () => {})();
  }, []);

  const createGame = async () => {
    console.log('creating game');
    const createGameViewModel = CreateGameViewModel.fromJson({
      state: GameState.NONE,
      stateTitle: gameStateToString(GameState.NONE),
      displayName: `${currentParticipant?.displayName}'s game`,
      type: GameType.CHALLENGE,
      typeTitle: gameTypeToString(GameType.CHALLENGE),
      ownerId: currentParticipant?.uid as TUserId,
    });

    const gameModel = await gameData.createGame(createGameViewModel);

    await client.mutate({
      mutation: gql`
        mutation StartGame($partyKey: String!, $areaKey: String!, $gameId: String!) {
          startGame(input: { partyKey: $partyKey, areaKey: $areaKey, gameId: $gameId }) {
            success
          }
        }
      `,
      variables: {
        partyKey: party?.key,
        areaKey: currentArea,
        gameId: gameModel.id,
      },
    });

    const newGame = new ChallengeGameClient(gameData, gameModel);

    const test = areaParticipants.filter(
      (participant) =>
        !gameDataState.selectedGamePlayers.find(
          (player) => player.foreignReferenceId === participant.uid
        )
    );

    areaParticipants
      .filter(
        (participant) =>
          !gameDataState.selectedGamePlayers.find(
            (player) => player.foreignReferenceId === participant.uid
          )
      )
      .forEach((participant) => {
        // console.log('participant for ', gameModel.id, ' and participantId ', participant.uid);
        const player: CreatePlayerViewModel = {
          foreignReferenceId: participant.uid,
          ownerId: currentParticipant?.uid as TUserId,
          points: 0,
          isOnDeck: false,
        };
        newGame.addPlayer(player);
      });
  };

  const area = party?.areas[currentArea];

  useEffect(() => {
    (async () => {
      if (!area?.currentGameId) {
        gameDataDispatch({ type: GameDetailActionType.RESET_STORE });
        return;
      }

      const gameModel = await gameData.getGame(area?.currentGameId);
      const newGame = new ChallengeGameClient(gameData, gameModel);

      setGameClient(newGame);
    })();
  }, [area?.currentGameId]);

  useEffect(() => {
    if (!gameClient) {
      return;
    }

    const gameDetailSubscription = gameClient.onGameDetailUpdateEvent.subscribe((game) => {
      // console.log('on update effect', game);
      gameDataDispatch({ type: GameDetailActionType.SET_GAME_DETAIL, detail: game });
    });
    const playerCollectionSubscripton = gameClient.onGamePlayerCollectionUpdateEvent.subscribe(
      (players) => {
        // console.log('on game player update effect', players);
        gameDataDispatch({ type: GameDetailActionType.SET_GAME_PLAYERS, players: players });
      }
    );
    const eventCollectionSubscripton = gameClient.onGameEventCollectionUpdateEvent.subscribe(
      (events) => {
        // console.log('on game events update effect', events);
        gameDataDispatch({ type: GameDetailActionType.SET_GAME_EVENTS, events });
      }
    );
    const errorSubscription = gameClient.onErrorEvent.subscribe((error) => {
      setError(error);
    });

    return () => {
      gameClient.destroy();
      gameDetailSubscription.unsubscribe();
      playerCollectionSubscripton.unsubscribe();
      eventCollectionSubscripton.unsubscribe();
      errorSubscription.unsubscribe();
      // setGameClient(null);
    };
  }, [gameClient]);

  const addPoint = (player: PlayerModel) => gameClient?.addPoint(player);
  const removePoint = (player: PlayerModel) => gameClient?.removePoint(player);
  const setGameDisplayName = async (displayName: string) => {
    await gameClient?.setGameDisplayName(displayName);
  };
  const startGame = async () => {
    await gameClient?.startGame();
  };
  const endGame = async () => {
    gameClient?.endGame();
  };

  const clearGameFromRoom = async () => {
    await client.mutate({
      mutation: gql`
        mutation EndGame($partyKey: String!, $areaKey: String!, $gameId: String!) {
          endGame(input: { partyKey: $partyKey, areaKey: $areaKey, gameId: $gameId }) {
            success
          }
        }
      `,
      variables: {
        partyKey: party?.key,
        areaKey: currentArea,
        gameId: gameDataState.selectedGameDetail?.id,
      },
    });
  };

  const leaveGame = async () => {
    const player = gameDataState.selectedGamePlayers.find(
      (player) => player.foreignReferenceId === currentParticipant?.uid
    );

    if (player) {
      gameClient?.removePlayer(player);

      // await joinArea({
      //   idToken: await getIdToken(),
      //   key: party?.key || ('' as TPartyKey),
      //   area: `${EAreaPrefix.LobbyPrefix}-0` as TAreaKey,
      // });
    }
  };

  // console.log(gameDataState.selectedGameDetail);

  return {
    gameDataState,
    gameDataError,
    gameDataDispatch,
    startGame,
    addPoint,
    removePoint,
    endGame,
    createGame,
    isOwner,
    leaveGame,
    clearGameFromRoom,
    setGameDisplayName,
    contextHolder,
  };
};

export const GameStateProvider = ({ children }: { children?: React.ReactNode }) => {
  const {
    gameDataState,
    gameDataError,
    gameDataDispatch,
    addPoint,
    removePoint,
    startGame,
    endGame,
    createGame,
    isOwner,
    leaveGame,
    clearGameFromRoom,
    setGameDisplayName,
    contextHolder,
  } = useChallengeGameState();
  return (
    <GameStateContext.Provider
      value={{
        gameDataState,
        gameDataError,
        gameDataDispatch,
        addPoint,
        removePoint,
        startGame,
        endGame,
        createGame,
        isOwner,
        leaveGame,
        clearGameFromRoom,
        setGameDisplayName,
      }}>
      {contextHolder}
      {children}
    </GameStateContext.Provider>
  );
};

const GameStateContext = createContext<IGameProviderProps>({
  gameDataState: initialGameReducerState,
  gameDataError: '',
  addPoint: (player: PlayerModel) => {},
  removePoint: (player: PlayerModel) => {},
  startGame: () => {},
  endGame: () => {},
  gameDataDispatch: (() => null) as Dispatch<TGameAction>,
  createGame: () => {},
  isOwner: false,
  leaveGame: () => Promise.resolve(),
  clearGameFromRoom: () => Promise.resolve(),
  setGameDisplayName: (name: string) => Promise.resolve(),
});

export const useGameState = () => {
  return useContext(GameStateContext);
};
